/**
 * /admin/tenant-config — admin form for the active LLM-judge configuration.
 *
 * MVP single-tenant single-active-version model. "Save" issues
 * POST /api/admin/tenant-config which auto-bumps the version number and
 * marks the previous active version 'rolled_back'.
 *
 * Fields kept minimal for now — operator only needs:
 *   - 业务描述 / LLM prompt (system prompt for the judge)
 *   - 关键词初筛 (cheap pre-filter; empty = LLM everything)
 *   - 置信度阈值
 *   - 开场白模板 (required by API but unused until outbound module lands)
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type TenantConfigVersionDetail,
  type TenantConfigVersionSummary,
  Routes,
} from '@tg-leads/contracts';

import { api, ApiError } from '../../lib/api';
import { friendlyError } from '../../lib/friendlyError';
import { toastError, toastSuccess } from '../../lib/toast';

const DEFAULT_PROMPT = `你是一个全行业销售线索检测器。

判断消息是否表达任何商业、销售、购买、合作或服务需求，覆盖所有行业类目（金融、加密货币、建材、五金、服装、电子、餐饮、物流、教育、医疗、广告、IT 服务、咨询等）。

不要局限于某一垂直领域 — 凡是表达"想买什么"、"想卖什么"、"找供应商"、"找客户"、"求合作"、"求报价"、"问价"、"招商加盟"、"产品需求"、"采购需求"等任何商业意向的消息，都算线索。

类目（category）和标签（tags）自由生成，准确反映消息内容（例如类目可以是"金融/建材/电子/服装/餐饮/物流/服务"，标签可以是"USDT/求购/量大/OTC"、"五金/锤子/批发"等）。`;

/**
 * Default noise description — embedded into noiseEmbedding (L1 anchor).
 * Operator should review and edit based on the specific groups they're in.
 */
const DEFAULT_NOISE = `以下类型的消息属于噪声（应被 L1 向量过滤）：

- 群规、群公告、入群欢迎、退群通知
- 加群链接广告、推广话术、刷单广告
- 机器人推送：签到打卡、骰子点歌、抽奖、点赞、查询命令回复
- 节日祝福、问候语、早安晚安、生日祝福
- 表情包、纯表情符号、纯标点
- 政治新闻、社会新闻、时政评论、八卦
- 闲聊吐槽、心情碎碎念、抱怨、骂战、刷屏
- 转账确认、群红包、领取通知、转账截图
- 星座运势、视频/电影/电视推荐
- 群成员变动通知（X 加入了 Y / 离开了）
- 个人签名、版本号、配置文件复制粘贴
- 政治政策法规摘抄`;

export function TenantConfigPage() {
  const qc = useQueryClient();

  const active = useQuery({
    queryKey: ['tenantConfig'],
    queryFn: () =>
      api
        .get<TenantConfigVersionDetail>(Routes.admin.tenantConfig)
        .catch((err) => {
          // 404 = no active version yet; treat as null so form starts blank.
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
  });

  const versions = useQuery({
    queryKey: ['tenantConfigVersions'],
    queryFn: () =>
      api
        .get<TenantConfigVersionSummary[]>(
          `${Routes.admin.tenantConfig}/versions`,
        )
        .catch(() => [] as TenantConfigVersionSummary[]),
  });

  const [llmPrompt, setLlmPrompt] = useState(DEFAULT_PROMPT);
  const [keywordsCsv, setKeywordsCsv] = useState('');
  const [noiseDescription, setNoiseDescription] = useState(DEFAULT_NOISE);
  // 3-tier cascade thresholds (Phase 9)
  const [embCosineMin, setEmbCosineMin] = useState(0.55);
  const [noiseCosineMax, setNoiseCosineMax] = useState(0.55);
  const [fastDropBelow, setFastDropBelow] = useState(0.4);
  const [fastSkipPreciseAt, setFastSkipPreciseAt] = useState(0.7);
  const [confidence, setConfidence] = useState(0.6);
  const [openerText, setOpenerText] = useState('您好，看到您在群里有相关需求，方便聊一下吗？');
  // Phase 8 outbound settings
  const [autoSendEnabled, setAutoSendEnabled] = useState(true);
  const [autoSendConf, setAutoSendConf] = useState(0.9);
  const [dailyDmCap, setDailyDmCap] = useState(20);
  const [cooldownMin, setCooldownMin] = useState(5);
  const [cooldownMax, setCooldownMax] = useState(15);
  const [quietStart, setQuietStart] = useState(0);
  const [quietEnd, setQuietEnd] = useState(7);
  const [recycleDays, setRecycleDays] = useState(7);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // When server returns an existing version, hydrate the form.
  useEffect(() => {
    if (!active.data) return;
    setLlmPrompt(active.data.llmPrompt);
    setKeywordsCsv(active.data.leadKeywords.join(','));
    setNoiseDescription(active.data.noiseDescription ?? DEFAULT_NOISE);
    const t = active.data.threshold ?? {};
    setEmbCosineMin(t.embeddingCosineMin ?? 0.55);
    setNoiseCosineMax(t.noiseCosineMax ?? 0.55);
    setFastDropBelow(t.fastDropBelow ?? 0.4);
    setFastSkipPreciseAt(t.fastSkipPreciseAt ?? 0.7);
    setConfidence(t.llmConfidenceMin ?? 0.6);
    setOpenerText(active.data.openerTemplates[0]?.text ?? '');
    const s = active.data.sendingStrategy ?? {};
    setAutoSendEnabled(s.autoSendEnabled ?? true);
    setAutoSendConf(s.autoSendConfThreshold ?? 0.9);
    setDailyDmCap(s.dailyDmPerAccount ?? 20);
    setCooldownMin(s.intervalMinMinutes ?? 5);
    setCooldownMax(s.intervalMaxMinutes ?? 15);
    setQuietStart(s.quietHoursStart ?? 0);
    setQuietEnd(s.quietHoursEnd ?? 7);
    setRecycleDays(s.noReplyRecycleDays ?? 7);
  }, [active.data]);

  const saveMut = useMutation({
    mutationFn: () => {
      const keywords = keywordsCsv
        .split(/[,，\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
      return api.post<TenantConfigVersionDetail>(Routes.admin.tenantConfig, {
        llmPrompt: llmPrompt.trim(),
        leadKeywords: keywords,
        noiseDescription: noiseDescription.trim() || undefined,
        openerTemplates: [
          { id: 'default', text: openerText.trim(), weight: 1 },
        ],
        threshold: {
          embeddingCosineMin: embCosineMin,
          noiseCosineMax: noiseCosineMax,
          fastDropBelow: fastDropBelow,
          fastSkipPreciseAt: fastSkipPreciseAt,
          llmConfidenceMin: confidence,
        },
        sendingStrategy: {
          dailyDmPerAccount: dailyDmCap,
          intervalMinMinutes: cooldownMin,
          intervalMaxMinutes: cooldownMax,
          prelude: ['view_profile', 'random_sleep'],
          autoSendEnabled,
          autoSendConfThreshold: autoSendConf,
          quietHoursStart: quietStart,
          quietHoursEnd: quietEnd,
          noReplyRecycleDays: recycleDays,
        },
        trafficPct: 100,
      });
    },
    onSuccess: () => {
      setSavedAt(new Date());
      setErr(null);
      qc.invalidateQueries({ queryKey: ['tenantConfig'] });
    },
    onError: (e: unknown) =>
      setErr(friendlyError(e)),
  });

  const [simulatorOpen, setSimulatorOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">业务描述与阈值</h1>
          <p className="text-sm text-slate-500 mt-1">
            描述你做什么业务、什么样的消息算线索，设置三层模型的置信度阈值。保存会建一个新版本，
            上一版本自动归档。
          </p>
        </div>
        <button
          className="btn-secondary whitespace-nowrap"
          onClick={() => setSimulatorOpen(true)}
        >
          🧪 用一条消息试一下
        </button>
      </div>

      {active.data && (
        <div className="text-xs text-slate-500 bg-slate-50 rounded p-3">
          当前生效版本：v{active.data.version} · 创建于{' '}
          {new Date(active.data.createdAt).toLocaleString()}
        </div>
      )}
      {savedAt && (
        <div className="text-xs text-green-700 bg-green-50 rounded p-3">
          ✓ 已保存（{savedAt.toLocaleTimeString()}），下一波消息进来就用新配置。
        </div>
      )}

      <div className="card space-y-4">
        <div>
          <label className="label">业务描述（给模型看的系统提示词）</label>
          <textarea
            className="input font-mono text-xs"
            rows={10}
            value={llmPrompt}
            onChange={(e) => setLlmPrompt(e.target.value)}
            placeholder="告诉模型你做什么业务，什么样的消息算线索。"
          />
          <p className="text-xs text-slate-400 mt-1">
            建议要求模型返回结构化 JSON（isLead / intent / confidence / ...），管线只会读这几个字段。
          </p>
        </div>
        <div>
          <label className="label">L0 关键词初筛（逗号或换行分隔，可留空）</label>
          <textarea
            className="input"
            rows={2}
            value={keywordsCsv}
            onChange={(e) => setKeywordsCsv(e.target.value)}
            placeholder="留空 = 所有消息都进 L1 向量过滤（推荐，覆盖全行业）"
          />
          <p className="text-xs text-slate-400 mt-1">
            L0 是最便宜的过滤层（纯字符串匹配）。<strong>留空 = 不做关键词过滤，全部进 L1 向量</strong>。
            如果群里 90% 消息都不相关，可以加几个常见商业词省 GPU。
          </p>
        </div>

        <div>
          <label className="label">L1 噪声描述（兜底用的旧版单向量；推荐用"噪声样本库"）</label>
          <textarea
            className="input font-mono text-xs"
            rows={8}
            value={noiseDescription}
            onChange={(e) => setNoiseDescription(e.target.value)}
            placeholder="描述什么算噪声（群规 / 广告 / 闲聊 / 表情包...）"
          />
          <p className="text-xs text-slate-400 mt-1">
            <strong>这段文本会被压成 1024 维向量，每条入站消息和它算余弦相似度，越像噪声相似度越高，超过阈值就丢。</strong>
            新方案推荐用"噪声样本库"页里的多锚点；这里的单段描述只在样本库为空时兜底。保存时会同步算向量（约 300 毫秒）。
          </p>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            四层判断阈值（本地 Qwen 模型）
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            L0 关键词 → L1 向量过滤 → L2 4B 快筛 → L3 14B 精筛 → 创建线索。每层有独立的"丢弃 / 放行"规则。
          </p>
          <div className="space-y-3">
            <div>
              <label className="label">
                L1 噪声相似度上限：≥ {noiseCosineMax.toFixed(2)} 视为噪声直接丢弃
              </label>
              <input
                type="range"
                min={-1}
                max={1}
                step={0.05}
                value={noiseCosineMax}
                onChange={(e) => setNoiseCosineMax(Number(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-slate-400 mt-1">
                消息向量与噪声向量的余弦相似度 ≥ 此值 → 判为噪声丢弃。
                降低 = 更激进过滤（误杀风险）；升高 = 更宽松（更多消息进 L2，成本上升）。默认 0.55。
              </p>
            </div>
            <details className="text-xs text-slate-400">
              <summary className="cursor-pointer">旧版"业务相似度"阈值（只在没设噪声描述时生效）</summary>
              <div className="mt-2">
                <label className="label">
                  业务相似度下限：≥ {embCosineMin.toFixed(2)} 才放行
                </label>
                <input
                  type="range"
                  min={-1}
                  max={1}
                  step={0.05}
                  value={embCosineMin}
                  onChange={(e) => setEmbCosineMin(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </details>
            <div>
              <label className="label">
                L2 快筛丢弃线：置信度 &lt; {fastDropBelow.toFixed(2)} 直接丢
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={fastDropBelow}
                onChange={(e) => setFastDropBelow(Number(e.target.value))}
                className="w-full"
              />
            </div>
            <div>
              <label className="label">
                L2 快筛短路线：置信度 ≥ {fastSkipPreciseAt.toFixed(2)} 跳过 14B 直接创建线索
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={fastSkipPreciseAt}
                onChange={(e) => setFastSkipPreciseAt(Number(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-slate-400 mt-1">
                4B 置信度落在 [丢弃线, 短路线) 区间的样本会升级到 14B 精筛。这条线越高，14B 调用就越多。
              </p>
            </div>
            <div>
              <label className="label">
                L3 精筛最终置信度阈值：≥ {(confidence * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={confidence}
                onChange={(e) => setConfidence(Number(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-slate-400 mt-1">
                14B 输出的置信度达到这个比例才创建线索。默认 60%。
              </p>
            </div>
            <LlmEndpointsTest />
          </div>
        </div>
        <div>
          <label className="label">默认开场白模板（自动外联发出的第一句）</label>
          <input
            className="input"
            value={openerText}
            onChange={(e) => setOpenerText(e.target.value)}
            placeholder="您好，看到您在群里有相关需求…"
          />
          <p className="text-xs text-slate-400 mt-1">
            自动外联时随机使用这条模板。占位符 {'{firstName}'} / {'{username}'} 暂时会被替换为空字符。
          </p>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            自动外联设置
          </h3>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoSendEnabled}
                onChange={(e) => setAutoSendEnabled(e.target.checked)}
              />
              启用自动外联
              <span className="text-xs text-slate-400">
                （关闭则所有线索都直接进销售抢单池）
              </span>
            </label>
            <div>
              <label className="label">
                自动外联触发置信度：≥ {(autoSendConf * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={autoSendConf}
                onChange={(e) => setAutoSendConf(Number(e.target.value))}
                className="w-full"
                disabled={!autoSendEnabled}
              />
              <p className="text-xs text-slate-400 mt-1">
                置信度高于这个值的线索会进自动外联队列；其它的进销售抢单池。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label text-xs">每账号每日私信上限</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  max={200}
                  value={dailyDmCap}
                  onChange={(e) => setDailyDmCap(Number(e.target.value))}
                  disabled={!autoSendEnabled}
                />
              </div>
              <div>
                <label className="label text-xs">两次发送最短间隔（分钟）</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  value={cooldownMin}
                  onChange={(e) => setCooldownMin(Number(e.target.value))}
                  disabled={!autoSendEnabled}
                />
              </div>
              <div>
                <label className="label text-xs">两次发送最长间隔（分钟）</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  value={cooldownMax}
                  onChange={(e) => setCooldownMax(Number(e.target.value))}
                  disabled={!autoSendEnabled}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label text-xs">静音开始（小时，0-23）</label>
                <input
                  type="number"
                  className="input"
                  min={0}
                  max={23}
                  value={quietStart}
                  onChange={(e) => setQuietStart(Number(e.target.value))}
                  disabled={!autoSendEnabled}
                />
              </div>
              <div>
                <label className="label text-xs">静音结束（小时，0-23）</label>
                <input
                  type="number"
                  className="input"
                  min={0}
                  max={23}
                  value={quietEnd}
                  onChange={(e) => setQuietEnd(Number(e.target.value))}
                  disabled={!autoSendEnabled}
                />
              </div>
              <div>
                <label className="label text-xs">几天没回复就退回抢单池</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  max={60}
                  value={recycleDays}
                  onChange={(e) => setRecycleDays(Number(e.target.value))}
                  disabled={!autoSendEnabled}
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              静音时段按服务器本地时区。超过时限仍未回复的线索会转回"待认领"状态，
              销售可以人工换开场白再发一轮。
            </p>
          </div>
        </div>

        {err && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
            {err}
          </div>
        )}
        <div className="flex justify-end">
          <button
            className="btn-primary"
            onClick={() => saveMut.mutate()}
            disabled={
              saveMut.isPending || !llmPrompt.trim() || !openerText.trim()
            }
          >
            {saveMut.isPending ? '保存中…' : '保存为新版本'}
          </button>
        </div>
      </div>

      {simulatorOpen && (
        <CascadeSimulator onClose={() => setSimulatorOpen(false)} />
      )}

      <div className="card">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">版本历史</h2>
        <p className="text-xs text-slate-500 mb-3">
          每次保存都会生成新版本号、把上一版自动归档。点"回滚到此版本"会
          <strong>复制</strong>该版本的所有字段创建一条新生效版本（版本号 +1），不会直接激活旧行。
        </p>
        {versions.isLoading ? (
          <div className="text-xs text-slate-500">加载中…</div>
        ) : !versions.data?.length ? (
          <div className="text-xs text-slate-500">还没有版本</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase border-b">
                <th className="py-2 pr-4">版本号</th>
                <th className="py-2 pr-4">状态</th>
                <th className="py-2 pr-4">流量比例</th>
                <th className="py-2 pr-4">生效时间</th>
                <th className="py-2 pr-4">归档时间</th>
                <th className="py-2 pr-4">创建时间</th>
                <th className="py-2 pr-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {versions.data.map((v) => (
                <VersionRow key={v.id} v={v} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * One row of the version history table. Encapsulates the rollback mutation
 * so each row owns its own loading state without a global isPending block.
 */
function VersionRow({ v }: { v: TenantConfigVersionSummary }) {
  const qc = useQueryClient();
  const restore = useMutation({
    mutationFn: () =>
      api.post<TenantConfigVersionDetail>(
        Routes.admin.tenantConfigVersionRestore(v.id),
      ),
    onSuccess: (data) => {
      toastSuccess(`已回滚到 v${v.version} 的内容，新建生效版本 v${data.version}`);
      qc.invalidateQueries({ queryKey: ['tenantConfig'] });
    },
    onError: (e) => toastError(e),
  });

  const isActive = v.status === 'active';
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-4 font-mono">v{v.version}</td>
      <td className="py-2 pr-4">
        <span
          className={
            v.status === 'active'
              ? 'badge-ok'
              : v.status === 'rolled_back'
                ? 'badge-neutral'
                : 'badge-warn'
          }
        >
          {v.status === 'active'
            ? '当前生效'
            : v.status === 'rolled_back'
              ? '已归档'
              : v.status === 'draft'
                ? '草稿'
                : v.status}
        </span>
      </td>
      <td className="py-2 pr-4">{v.trafficPct}%</td>
      <td className="py-2 pr-4 text-slate-500">
        {v.activatedAt ? new Date(v.activatedAt).toLocaleString() : '—'}
      </td>
      <td className="py-2 pr-4 text-slate-500">
        {v.rolledBackAt ? new Date(v.rolledBackAt).toLocaleString() : '—'}
      </td>
      <td className="py-2 pr-4 text-slate-500">
        {new Date(v.createdAt).toLocaleString()}
      </td>
      <td className="py-2 pr-4">
        <button
          className="btn-secondary text-xs"
          disabled={isActive || restore.isPending}
          title={isActive ? '当前生效版本无需回滚' : `复制 v${v.version} 的内容创建新生效版本`}
          onClick={() => {
            if (
              confirm(
                `确定要回滚到 v${v.version}？\n\n会复制该版本的所有字段创建一条新生效版本，` +
                  `当前生效版本会自动归档。原 v${v.version} 不会被修改。`,
              )
            ) {
              restore.mutate();
            }
          }}
        >
          {restore.isPending ? '回滚中…' : '回滚到此版本'}
        </button>
      </td>
    </tr>
  );
}

/**
 * "Test LLM endpoints" — fires one probe against each tier, shows OK/latency
 * + raw sample. Lets the operator verify QWEN_*_URL config is reachable
 * without waiting for a real lead to come through the pipeline.
 */
interface TierTestResult {
  ok: boolean;
  configured: boolean;
  latencyMs: number;
  model: string | null;
  sample: string | null;
  error: string | null;
}
interface LlmTestResponse {
  embedding: TierTestResult;
  fast: TierTestResult;
  precise: TierTestResult;
}

function LlmEndpointsTest() {
  const mut = useMutation({
    mutationFn: () =>
      api.post<LlmTestResponse>('/api/admin/llm/test', {}),
  });

  return (
    <div className="mt-4 border-t pt-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">
          模型端点连通测试
        </h4>
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
        >
          {mut.isPending ? '测试中…' : '一键测试三层'}
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-1">
        给三层端点各发一条测试消息（"USDT 求购，量大价优，加微信详谈"），返回延迟和模型回包样本。
        没配的层会显示"未配置"。
      </p>

      {mut.data && (
        <div className="mt-3 space-y-2">
          <TierRow label="L1 向量（embedding）" r={mut.data.embedding} />
          <TierRow label="L2 快筛（4B）" r={mut.data.fast} />
          <TierRow label="L3 精筛（14B）" r={mut.data.precise} />
        </div>
      )}
      {mut.isError && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 px-3 py-2 rounded">
          请求失败：{(mut.error as Error).message}
        </div>
      )}
    </div>
  );
}

function TierRow({ label, r }: { label: string; r: TierTestResult }) {
  const status = !r.configured
    ? { tone: 'bg-slate-100 text-slate-600 border-slate-200', text: '未配置' }
    : r.ok
      ? { tone: 'bg-green-50 text-green-800 border-green-200', text: `正常 · ${r.latencyMs}ms` }
      : { tone: 'bg-red-50 text-red-800 border-red-200', text: '失败' };
  return (
    <div className={`border rounded p-2 ${status.tone}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className="font-mono">{status.text}</span>
      </div>
      {r.model && (
        <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
          模型: {r.model}
        </div>
      )}
      {r.sample && (
        <div className="text-[10px] mt-0.5 font-mono break-all">{r.sample}</div>
      )}
      {r.error && (
        <div className="text-[10px] mt-0.5 font-mono break-all text-red-700">
          {r.error}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Cascade Simulator — paste a sample message → see L0..L3 verdict in real time
// ============================================================================

const SAMPLE_TEXTS = [
  'USDT 求购，量大价优，加我微信',
  '出 5万U，需要请联系',
  '@everyone 群规请遵守，禁止发广告',
  '今天天气真不错，我去吃个饭',
  '哪里能买到 USDT? 汇率多少?',
];

interface SimulateResult {
  text: string;
  decision: 'lead' | 'drop';
  decidedBy: 'fast' | 'precise' | null;
  droppedAt: string | null;
  l0: { enabled: boolean; matchedKeywords: string[]; passed: boolean };
  l1: {
    enabled: boolean;
    mode: 'multi_anchor' | 'noise_exclude' | 'business_include' | 'none';
    cosine: number | null;
    threshold: number;
    passed: boolean;
    latencyMs: number | null;
    anchorCount: number | null;
    bestAnchorText: string | null;
    error: string | null;
  };
  l2: {
    enabled: boolean;
    confidence: number | null;
    dropBelow: number;
    skipPreciseAt: number;
    passed: boolean;
    shortCircuited: boolean;
    category: string | null;
    tags: string[];
    latencyMs: number | null;
    error: string | null;
  };
  l3: {
    enabled: boolean;
    confidence: number | null;
    threshold: number;
    passed: boolean;
    category: string | null;
    tags: string[];
    intent: string | null;
    rationale: string | null;
    suggestedOpener: string | null;
    latencyMs: number | null;
    error: string | null;
  };
  totalLatencyMs: number;
}

function CascadeSimulator({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState(SAMPLE_TEXTS[0]!);

  const run = useMutation({
    mutationFn: (input: string) =>
      api.post<SimulateResult>(Routes.admin.tenantConfigSimulate, {
        text: input,
      }),
  });

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <aside
        className="absolute right-0 top-0 h-full w-[680px] bg-white shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                🧪 Cascade 模拟器
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                试一条消息看四层裁决。直接调当前 active 配置的 L0→L1→L2→L3
                全链路，<strong>不写入 lead、不计费</strong>。每次调用会真实命中
                Qwen 服务端（L1+L2+L3）。
              </p>
            </div>
            <button
              className="text-slate-400 hover:text-slate-700 text-xl leading-none"
              onClick={onClose}
              aria-label="关闭"
            >
              ×
            </button>
          </div>

          <div>
            <label className="label">消息文本</label>
            <textarea
              className="input font-mono text-xs"
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="粘贴一段 TG 群里出现过的消息"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-xs text-slate-400 self-center">示例：</span>
              {SAMPLE_TEXTS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="text-xs text-indigo-600 hover:underline"
                  onClick={() => setText(s)}
                >
                  {s.slice(0, 12)}…
                </button>
              ))}
            </div>
          </div>

          <button
            className="btn-primary"
            disabled={!text.trim() || run.isPending}
            onClick={() => run.mutate(text.trim())}
          >
            {run.isPending ? '推理中…（可能 5-10s）' : '运行 Cascade'}
          </button>

          {run.error && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              {friendlyError(run.error)}
            </div>
          )}

          {run.data && <SimulateResultPanel r={run.data} />}
        </div>
      </aside>
    </div>
  );
}

function SimulateResultPanel({ r }: { r: SimulateResult }) {
  const isLead = r.decision === 'lead';
  // L2/L3 said NOT a lead — strong signal admin might want to encode
  // this as a noise anchor so future similar messages get killed at L1.
  const shouldOfferAddAsNoise =
    !isLead &&
    r.droppedAt != null &&
    ['fast_dropped', 'precise_dropped', 'precise_low_conf'].includes(
      r.droppedAt,
    );

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`text-lg font-bold ${
            isLead ? 'text-green-700' : 'text-slate-500'
          }`}
        >
          {isLead ? '✓ 创建 LEAD' : '✗ 丢弃'}
        </span>
        {isLead && r.decidedBy && (
          <span className="text-xs text-slate-500">
            裁决层: {r.decidedBy === 'fast' ? 'L2 4B（短路）' : 'L3 14B'}
          </span>
        )}
        {!isLead && r.droppedAt && (
          <span className="text-xs text-slate-500">
            停在: {dropStageLabel(r.droppedAt)}
          </span>
        )}
        <span className="text-xs text-slate-400 ml-auto">
          总耗时 {r.totalLatencyMs}ms
        </span>
      </div>

      {shouldOfferAddAsNoise && (
        <AddAsNoiseShortcut text={r.text} droppedAt={r.droppedAt!} />
      )}

      <L0Card l0={r.l0} />
      <L1Card l1={r.l1} />
      <L2Card l2={r.l2} />
      <L3Card l3={r.l3} decision={r.decision} decidedBy={r.decidedBy} />
    </div>
  );
}

// ============================================================================
// Unified layer card — every layer renders the same 5-row layout:
//   header (status icon · label · latency)
//   model   — which model/source ran
//   metric  — numeric measurement (cosine / confidence / keyword count)
//   condition — threshold expression
//   verdict — one-line conclusion
//   extras  — layer-specific bonus rows (anchor / category / opener)
// ============================================================================

type LayerStatus = 'passed' | 'dropped' | 'skipped' | 'errored';

interface LayerCardProps {
  layerLabel: string;
  modelLabel: string;
  status: LayerStatus;
  latencyMs: number | null;
  metric: React.ReactNode;
  condition: string;
  verdict: string;
  error?: string | null;
  extras?: React.ReactNode;
}

function LayerCard({
  layerLabel,
  modelLabel,
  status,
  latencyMs,
  metric,
  condition,
  verdict,
  error,
  extras,
}: LayerCardProps) {
  const palette = {
    passed: { icon: '✓', border: 'border-green-400', text: 'text-green-700' },
    dropped: { icon: '✗', border: 'border-red-400', text: 'text-red-700' },
    skipped: { icon: '⏭', border: 'border-slate-300', text: 'text-slate-400' },
    errored: { icon: '⚠', border: 'border-orange-400', text: 'text-orange-700' },
  }[status];

  return (
    <div className={`border-l-4 ${palette.border} pl-3 py-2`}>
      <div className="flex items-center gap-2 text-xs">
        <span className={palette.text}>{palette.icon}</span>
        <span className="font-medium text-slate-700">{layerLabel}</span>
        <span className="text-slate-400 ml-auto font-mono">
          {latencyMs != null ? `${latencyMs}ms` : '—'}
        </span>
      </div>
      <div className="ml-5 mt-1.5 grid grid-cols-[3.5rem_1fr] gap-x-2 gap-y-1 text-xs">
        <span className="text-slate-400">模型</span>
        <span className="text-slate-600">{modelLabel}</span>
        <span className="text-slate-400">指标</span>
        <span className="text-slate-600 font-mono">{metric}</span>
        <span className="text-slate-400">条件</span>
        <span className="text-slate-600 font-mono">{condition}</span>
        <span className="text-slate-400">结论</span>
        <span className={palette.text}>{verdict}</span>
        {error && (
          <>
            <span className="text-orange-600">错误</span>
            <span className="text-orange-700 font-mono break-all">{error}</span>
          </>
        )}
      </div>
      {extras && <div className="ml-5 mt-2 space-y-1 text-xs">{extras}</div>}
    </div>
  );
}

// ---- Per-layer derivations (status + verbal verdict + extras) ----

function L0Card({ l0 }: { l0: SimulateResult['l0'] }) {
  const status: LayerStatus = !l0.enabled
    ? 'skipped'
    : l0.passed
      ? 'passed'
      : 'dropped';
  const metric = l0.enabled
    ? l0.matchedKeywords.length > 0
      ? `命中 ${l0.matchedKeywords.length} 个关键词`
      : '未命中任何关键词'
    : '（未启用）';
  const condition = l0.enabled ? '至少 1 个关键词命中' : '——';
  const verdict = !l0.enabled
    ? '未配置 — 所有消息进 L1'
    : l0.passed
      ? '命中 → 进 L1'
      : '无命中 → 丢弃';
  const extras = l0.enabled && l0.matchedKeywords.length > 0 && (
    <div className="text-slate-500">
      命中：{l0.matchedKeywords.map((k) => `「${k}」`).join('、')}
    </div>
  );
  return (
    <LayerCard
      layerLabel="L0 · 关键词预筛"
      modelLabel="本地 string match（无 LLM）"
      status={status}
      latencyMs={null}
      metric={metric}
      condition={condition}
      verdict={verdict}
      extras={extras}
    />
  );
}

function L1Card({ l1 }: { l1: SimulateResult['l1'] }) {
  const status: LayerStatus = !l1.enabled
    ? 'skipped'
    : l1.error
      ? 'errored'
      : l1.passed
        ? 'passed'
        : 'dropped';

  const modeLabel =
    l1.mode === 'multi_anchor'
      ? `多 anchor（${l1.anchorCount ?? 0} 条）`
      : l1.mode === 'noise_exclude'
        ? '单向量噪声（legacy）'
        : l1.mode === 'business_include'
          ? '单向量业务相似（legacy）'
          : '未配置';

  const metric =
    !l1.enabled
      ? '（未启用）'
      : l1.cosine == null
        ? '—'
        : l1.mode === 'multi_anchor'
          ? `max cosine = ${l1.cosine.toFixed(4)}`
          : `cosine = ${l1.cosine.toFixed(4)}`;

  const condition = !l1.enabled
    ? '——'
    : l1.mode === 'business_include'
      ? `≥ ${l1.threshold.toFixed(2)}（业务相似才放行）`
      : `< ${l1.threshold.toFixed(2)}（超过即视为噪声）`;

  const verdict = !l1.enabled
    ? '未配置 — 跳过 L1'
    : l1.error
      ? '调用失败 — 已 fail-open 进 L2'
      : l1.mode === 'multi_anchor'
        ? l1.passed
          ? '不像任何 anchor → 通过'
          : `匹配 anchor「${l1.bestAnchorText ?? '?'}」 → 丢弃`
        : l1.mode === 'business_include'
          ? l1.passed
            ? '业务相关 → 通过'
            : '不够相关 → 丢弃'
          : l1.passed
            ? '不像噪声 → 通过'
            : '太像噪声 → 丢弃';

  const extras = l1.mode === 'multi_anchor' && l1.bestAnchorText && l1.enabled && !l1.error && (
    <div className="text-slate-500">
      最佳匹配 anchor：
      <span className="font-mono bg-slate-50 px-1.5 py-0.5 rounded ml-1">
        {l1.bestAnchorText.slice(0, 80)}
        {l1.bestAnchorText.length > 80 ? '…' : ''}
      </span>
    </div>
  );

  return (
    <LayerCard
      layerLabel={`L1 · 向量过滤（${modeLabel}）`}
      modelLabel="Qwen3-Embedding-0.6B"
      status={status}
      latencyMs={l1.latencyMs}
      metric={metric}
      condition={condition}
      verdict={verdict}
      error={l1.error}
      extras={extras}
    />
  );
}

function L2Card({ l2 }: { l2: SimulateResult['l2'] }) {
  const status: LayerStatus = !l2.enabled
    ? 'skipped'
    : l2.error
      ? 'errored'
      : l2.shortCircuited
        ? 'passed'
        : l2.passed
          ? 'passed'
          : 'dropped';

  const metric =
    !l2.enabled
      ? '（未启用）'
      : l2.confidence == null
        ? '—'
        : `confidence = ${l2.confidence.toFixed(3)}`;

  const condition = !l2.enabled
    ? '——'
    : `drop < ${l2.dropBelow.toFixed(2)} · skip-L3 ≥ ${l2.skipPreciseAt.toFixed(2)}`;

  const verdict = !l2.enabled
    ? '未配置 — QWEN_FAST_URL 未填，跳过 L2'
    : l2.error
      ? '调用失败 — 已 fail-open 进 L3'
      : l2.shortCircuited
        ? '高置信 → 跳过 L3，直接创 lead'
        : l2.passed
          ? '中等置信 → 升级 L3'
          : '低置信 → 丢弃';

  const extras = !l2.error && (l2.category || l2.tags.length > 0) && (
    <CategoryTagsRow category={l2.category} tags={l2.tags} />
  );

  return (
    <LayerCard
      layerLabel="L2 · 快筛"
      modelLabel="Qwen3-4B-Instruct"
      status={status}
      latencyMs={l2.latencyMs}
      metric={metric}
      condition={condition}
      verdict={verdict}
      error={l2.error}
      extras={extras}
    />
  );
}

function L3Card({
  l3,
  decision,
  decidedBy,
}: {
  l3: SimulateResult['l3'];
  decision: 'lead' | 'drop';
  decidedBy: 'fast' | 'precise' | null;
}) {
  const status: LayerStatus = !l3.enabled
    ? 'skipped'
    : l3.error
      ? 'errored'
      : l3.passed
        ? 'passed'
        : 'dropped';

  const metric =
    !l3.enabled
      ? '（未运行）'
      : l3.confidence == null
        ? '—'
        : `confidence = ${l3.confidence.toFixed(3)}`;

  const condition = !l3.enabled
    ? '——'
    : `isLead=true 且 confidence ≥ ${l3.threshold.toFixed(2)}`;

  const skippedReason =
    decision === 'lead' && decidedBy === 'fast'
      ? '未运行 — L2 短路直接创 lead'
      : '未运行 — 前层已 drop';

  const verdict = !l3.enabled
    ? skippedReason
    : l3.error
      ? '调用失败 — 无法判断'
      : l3.passed
        ? '高置信 → 创建 lead'
        : l3.confidence != null && l3.confidence < l3.threshold
          ? '置信不足 → 丢弃'
          : 'isLead=false → 丢弃';

  const extras =
    !l3.error &&
    l3.enabled &&
    (l3.category || l3.tags.length > 0 || l3.intent || l3.suggestedOpener || l3.rationale) && (
      <>
        {(l3.category || l3.tags.length > 0) && (
          <CategoryTagsRow category={l3.category} tags={l3.tags} />
        )}
        {l3.intent && (
          <div className="text-slate-600">
            意图：<span className="font-semibold">{l3.intent}</span>
          </div>
        )}
        {l3.suggestedOpener && (
          <div className="bg-slate-50 px-2 py-1 rounded text-slate-700">
            开场白：{l3.suggestedOpener}
          </div>
        )}
        {l3.rationale && (
          <div className="text-slate-500 italic">{l3.rationale}</div>
        )}
      </>
    );

  return (
    <LayerCard
      layerLabel="L3 · 精筛"
      modelLabel="Qwen3-14B-AWQ"
      status={status}
      latencyMs={l3.latencyMs}
      metric={metric}
      condition={condition}
      verdict={verdict}
      error={l3.error}
      extras={extras}
    />
  );
}

function CategoryTagsRow({
  category,
  tags,
}: {
  category: string | null;
  tags: string[];
}) {
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {category && (
        <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
          类目: {category}
        </span>
      )}
      {tags.map((t) => (
        <span
          key={t}
          className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
        >
          #{t}
        </span>
      ))}
    </div>
  );
}

function dropStageLabel(s: string): string {
  switch (s) {
    case 'no_keyword':
      return 'L0 — 没命中关键词';
    case 'no_embedding':
      return 'L1 — 业务向量缺失';
    case 'low_cosine':
      return 'L1 — cosine 低于阈值';
    case 'fast_dropped':
      return 'L2 — 4B 明确否决';
    case 'precise_dropped':
      return 'L3 — 14B 判 isLead=false';
    case 'precise_low_conf':
      return 'L3 — 14B confidence 不够';
    case 'high_noise_cosine':
      return 'L1 — 噪声向量命中 (cosine 太高)';
    default:
      return s;
  }
}

/**
 * Shortcut shown in simulator results when L2/L3 said "not a lead" but L1
 * let the message through — perfect candidate to add as a new noise anchor
 * so similar future messages get killed earlier in the pipeline.
 */
function AddAsNoiseShortcut({
  text,
  droppedAt,
}: {
  text: string;
  droppedAt: string;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.post(Routes.admin.noiseAnchors, {
        text,
        note: note.trim() || `via simulator (${droppedAt})`,
      }),
    onSuccess: () => {
      toastSuccess('已加为噪声 anchor，30s 内 L1 生效');
      setOpen(false);
      setNote('');
    },
    onError: (e) => toastError(e),
  });

  return (
    <div className="bg-orange-50 border border-orange-200 rounded p-2">
      {!open ? (
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-orange-900">
            <strong>L2/L3 已判为非线索</strong> — 这条没被 L1 拦下来，要不要加为噪声样本？
            加了之后类似消息将在 L1 直接 drop，省 GPU。
          </div>
          <button
            className="btn-secondary text-xs whitespace-nowrap"
            onClick={() => setOpen(true)}
          >
            + 加为噪声样本
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-slate-600">
            原文：
            <span className="font-mono bg-white px-2 py-0.5 rounded ml-1">
              {text.slice(0, 80)}
              {text.length > 80 && '…'}
            </span>
          </div>
          <input
            className="input text-xs"
            placeholder="备注（可选，默认填 via simulator）"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <button
              className="btn-secondary text-xs"
              onClick={() => setOpen(false)}
            >
              取消
            </button>
            <button
              className="btn-primary text-xs"
              disabled={add.isPending}
              onClick={() => add.mutate()}
            >
              {add.isPending ? '保存中…' : '确认添加'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
